import { ChatErrorState } from '@/modules/common/Chat/components/ChatStatusRow';
import { ChatTranscript } from '@/modules/common/Chat/components/ChatTranscript';
import { isInteractiveMessage } from '@/modules/household/chat/components/messages/interactive/interactiveMessage.utils';
import { MessageRole, MessageType } from '@/types/schema';

import {
  HouseholdChatMessage,
  useHouseholdChatContext,
} from '../contexts/householdChat.context';
import { HouseholdChatAssistantMessage } from './HouseholdChatAssistantMessage';
import { HouseholdChatUserMessage } from './HouseholdChatUserMessage';

/**
 * Returns false for rows that the transcript must not show:
 * - A GENERATED_USER_RESPONSE row is the user's answer to an interactive
 *   message. The interactive message already shows the answer in place
 *   (through its responseFor edge). All chat instances hide these rows.
 * - A row that the chat instance hides through its transcript options.
 */
function isVisibleTranscriptMessage(
  message: HouseholdChatMessage,
  isTranscriptMessageHidden: ((message: HouseholdChatMessage) => boolean) | null
): boolean {
  if (message.type === MessageType.GeneratedUserResponse) return false;
  return !isTranscriptMessageHidden?.(message);
}

function HouseholdChatMessageRow({
  isStreaming = false,
  message,
}: {
  isStreaming?: boolean;
  message: HouseholdChatMessage;
}) {
  return message.role === MessageRole.User ? (
    <HouseholdChatUserMessage message={message} />
  ) : (
    <HouseholdChatAssistantMessage
      message={message}
      isStreaming={isStreaming}
    />
  );
}

export function HouseholdChatTranscript() {
  const {
    clearPendingScroll,
    error,
    isTranscriptMessageHidden,
    messages,
    pendingScrollToMessageId,
    streamingMessageId,
  } = useHouseholdChatContext();

  const visibleMessages = messages.filter((message) =>
    isVisibleTranscriptMessage(message, isTranscriptMessageHidden)
  );

  return (
    <ChatTranscript
      messages={visibleMessages}
      getMessageGap={(message, previousMessage) =>
        isInteractiveMessage(message) && isInteractiveMessage(previousMessage)
          ? 0.5
          : undefined
      }
      onScrollTargetReached={clearPendingScroll}
      scrollToMessageId={pendingScrollToMessageId}
      errorState={
        error && messages.length > 0 ? (
          <ChatErrorState error={error} />
        ) : undefined
      }
      renderMessage={({ message }) => (
        <HouseholdChatMessageRow
          message={message}
          isStreaming={message.id === streamingMessageId}
        />
      )}
    />
  );
}
